import './assets/main.css'

import { createApp } from 'vue'
import { createPinia } from 'pinia'
import VueKonva from 'vue-konva'
import * as Sentry from '@sentry/vue'
import { logger } from '@/lib/logger'
import { Platform } from '@/lib/platform'

import App from './App.vue'
import router from './router'

// Mark Tauri environment on html element for CSS targeting
if (Platform.isDesktop()) {
  document.documentElement.setAttribute('data-tauri', 'true')
  logger.info('🖥️ [main] Tauri environment detected, enabling window drag region')

  // Suppress harmless cross-protocol iframe warnings from embedded YouTube under tauri://
  // (YouTube's player scripts attempt window.parent access; WebKit blocks but the embed still works)
  const origError = console.error
  console.error = (...args: any[]) => {
    const first = args[0]
    if (typeof first === 'string' && first.includes('Blocked a frame with origin "tauri://')) {
      return
    }
    origError.apply(console, args)
  }
}

// Apply saved theme immediately before app initialization
const applyTheme = () => {
  const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | 'midnight' | 'flashbang' | 'hell' | 'forest' | 'system' | null
  const theme = savedTheme || 'system'
  const root = document.documentElement
  
  // Remove all theme classes first
  root.classList.remove('dark', 'midnight', 'flashbang', 'hell', 'forest')
  
  if (theme === 'system') {
    const isDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    root.classList.toggle('dark', isDark)
  } else if (theme === 'dark') {
    root.classList.add('dark')
  } else if (theme === 'midnight') {
    root.classList.add('midnight')
  } else if (theme === 'flashbang') {
    root.classList.add('flashbang')
  } else if (theme === 'hell') {
    root.classList.add('hell')
  } else if (theme === 'forest') {
    root.classList.add('forest')
  }
}

// Apply theme before anything else renders
applyTheme()

// Clean up OAuth tokens from URL hash on non-callback routes
// This prevents tokens from leaking into the URL on page refreshes or direct navigation
// OAuth tokens should only be processed on /auth/callback
const cleanupAuthHash = () => {
  const hash = window.location.hash
  if (hash && (hash.includes('access_token') || hash.includes('refresh_token') || hash.includes('error_description'))) {
    // Only clean up if we're NOT on the auth callback route
    // The callback route will handle its own hash processing
    if (!window.location.pathname.includes('/auth/callback')) {
      logger.info('🔒 [main] Cleaning up OAuth tokens from URL hash')
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
    }
  }
}
cleanupAuthHash()

// Listen for system theme changes when using 'system' theme
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
  const savedTheme = localStorage.getItem('theme')
  if (savedTheme === 'system' || !savedTheme) {
    document.documentElement.classList.remove('midnight', 'flashbang', 'hell', 'forest')
    document.documentElement.classList.toggle('dark', e.matches)
  }
})

const app = createApp(App)
const pinia = createPinia()

// Initialize Sentry BEFORE mounting the app
Sentry.init({
  app,
  dsn: "https://c61cd1aadbfb714b715b275cbdc0a38d@o1083896.ingest.us.sentry.io/4509097351577600",
  environment: import.meta.env.MODE || 'development',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration(),
    // User Feedback integration - manually triggered via settings modal
    Sentry.feedbackIntegration({
      colorScheme: 'system',
      buttonLabel: 'Report Bug',
      submitButtonLabel: 'Send Report',
      formTitle: 'Report a Bug',
      messagePlaceholder: 'Describe what happened...',
      successMessageText: 'Thanks for your feedback!',
      isEmailRequired: false,
      isNameRequired: false,
      autoInject: false, // We add our own button in the settings modal
      showBranding: false,
    }),
  ],
  // Performance Monitoring
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.supabase\.co\//, /^http:\/\/localhost/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // Sample 10% of sessions in production
  replaysOnErrorSampleRate: 1.0, // Always capture replays when errors occur
  // Setting this option to true will send default PII data to Sentry.
  sendDefaultPii: true,
  // Log outgoing events for debugging
  beforeSend(event, hint) {
    console.log('📤 Sentry: Sending event', event.event_id, hint.originalException || hint.syntheticException)
    return event
  },
  // Capture unhandled promise rejections
  ignoreErrors: [
    // Add patterns for errors you want to ignore
    'ResizeObserver loop limit exceeded',
    'Non-Error promise rejection captured',
  ],
})

app.use(pinia)
app.use(router)
app.use(VueKonva)

// Vue Tour plugin for onboarding
import Vue3Tour from 'vue3-tour'
import 'vue3-tour/dist/vue3-tour.css'
app.use(Vue3Tour)

// Initialize auth store asynchronously
import { useAuthStore } from './stores/auth'

// Import Hue background service to start global sync
import { hueBackgroundService } from './services/hue/HueBackgroundService'

// Import WLED background service to start WLED sync
import { wledBackgroundService } from './services/wled/WledBackgroundService'

// Import native menu setup
import { setupNativeMenu } from './menu'

const initializeApp = async () => {
  const authStore = useAuthStore()
  await authStore.initialize()
  app.mount('#app')
  
  // Initialize native menu (only in Tauri)
  if ((window as any).__TAURI__) {
    await setupNativeMenu()
    
    // Add Ctrl+Shift+R shortcut to reload the window
    document.addEventListener('keydown', async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'r') {
        e.preventDefault()
        logger.info('Reloading window via Ctrl+Shift+R')
        const { getCurrentWebviewWindow } = await import('@tauri-apps/api/webviewWindow')
        const window = getCurrentWebviewWindow()
        // Reload by navigating to the same URL
        location.reload()
      }
    })
  }
  
  // Start Hue service after app is mounted and auth is ready
  try {
    logger.info('Starting Hue background service')
    await hueBackgroundService.initialize()
    logger.success('Hue background service started successfully')
  } catch (error) {
    logger.error('Failed to start Hue background service:', error)
  }
  
  // Start WLED service after app is mounted
  try {
    logger.info('Starting WLED background service')
    await wledBackgroundService.initialize()
    logger.success('WLED background service started successfully')
  } catch (error) {
    logger.error('Failed to start WLED background service:', error)
  }
}

initializeApp()
